import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOdoo } from '../../context/OdooContext.js';
import { useCctpProject } from '../../hooks/useCctpProject.js';
import { useMdd, LOT_ICONS } from '../../hooks/useMdd.js';
import FieldRenderer from '../../components/fields/FieldRenderer.js';
import type { MddSubLot, FieldValue, LotData } from '../../types.js';

function SubLotAccordion({
  subLot, lotData, onChange,
}: { subLot: MddSubLot; lotData: LotData | undefined; onChange: (field: string, val: FieldValue) => void }) {
  const [open, setOpen] = useState(false);
  const filled = subLot.fields.filter(f => {
    const v = lotData?.[f.name];
    return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;
  const pct = subLot.fields.length ? Math.round((filled / subLot.fields.length) * 100) : 0;

  return (
    <div className="border border-line rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-ink-3 w-8">{subLot.num}</span>
          <span className="text-sm font-semibold text-ink">{subLot.label}</span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-16 h-1 bg-line rounded-full overflow-hidden">
            <div className={`h-full rounded-full ${pct === 100 ? 'bg-green' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs text-ink-3 w-8 text-right">{pct}%</span>
          <span className={`text-ink-3 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        </div>
      </button>

      {open && (
        <div className="border-t border-line bg-gray-50/50 px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {subLot.fields.map(field => (
            <div key={field.name} className={field.type === 'text' ? 'md:col-span-2' : ''}>
              <label className="text-xs font-semibold text-ink-2 block mb-1.5">
                {field.label}
                {field.required && <span className="text-red ml-1">*</span>}
              </label>
              <FieldRenderer
                field={field}
                value={(lotData?.[field.name] as FieldValue) ?? null}
                onChange={val => onChange(field.name, val)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LotDetailScreen() {
  const { id, lotCode } = useParams<{ id: string; lotCode: string }>();
  const navigate = useNavigate();
  const { client } = useOdoo();
  const { project, lots, loading, setField, isDirty } = useCctpProject(client, id ? parseInt(id) : null);
  const mdd = useMdd();

  const currentLot = mdd.lots.find(l => l.code === lotCode) ?? mdd.lots[0];

  if (loading) return <div className="min-h-screen bg-bg flex items-center justify-center text-ink-3">Chargement…</div>;
  if (!project) return null;

  const lotData = lots[currentLot.code] as LotData | undefined;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-line px-6 py-3 flex items-center gap-3 shrink-0">
        <button onClick={() => navigate(`/projects/${id}/lots`)} className="text-ink-3 hover:text-ink text-sm transition-colors">
          ← {project.name}
        </button>
        <div className="h-4 w-px bg-line" />
        <span className="font-bold text-ink text-sm">{currentLot.label}</span>
        {isDirty && (
          <span className="ml-auto text-xs text-orange bg-orange-soft px-2 py-0.5 rounded-md">Sauvegarde…</span>
        )}
        {!isDirty && (
          <span className="ml-auto text-xs text-green bg-green-soft px-2 py-0.5 rounded-md">Sauvegardé</span>
        )}
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — list of 12 lots */}
        <aside className="w-64 bg-white border-r border-line flex flex-col shrink-0 overflow-y-auto scroll-hidden">
          {mdd.lots.map(lot => {
            const active = lot.code === currentLot.code;
            return (
              <button
                key={lot.code}
                onClick={() => navigate(`/projects/${id}/lots/${lot.code}`)}
                className={`flex items-center gap-3 px-4 py-3 text-left transition-colors border-l-2 ${
                  active ? 'bg-brand-soft border-brand text-brand-dark' : 'border-transparent hover:bg-gray-50 text-ink-2'
                }`}
              >
                <span className="text-base">{LOT_ICONS[lot.code]}</span>
                <div>
                  <div className="text-xs text-ink-3">Lot {lot.num}</div>
                  <div className={`text-xs font-semibold leading-tight ${active ? 'text-brand-dark' : 'text-ink'}`}>{lot.label}</div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto p-6 space-y-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-ink">{LOT_ICONS[currentLot.code]} {currentLot.label}</h2>
              <p className="text-xs text-ink-3 mt-0.5">{currentLot.sub_lots.length} sous-lots · {currentLot.sub_lots.reduce((a, s) => a + s.fields.length, 0)} champs</p>
            </div>
            {currentLot.estimation_default > 0 && (
              <div className="text-right">
                <p className="text-xs text-ink-3">Estimation lot</p>
                <p className="text-base font-bold text-ink">{currentLot.estimation_default.toLocaleString('fr-FR')} €</p>
              </div>
            )}
          </div>

          {currentLot.sub_lots.map(subLot => (
            <SubLotAccordion
              key={subLot.code}
              subLot={subLot}
              lotData={lotData}
              onChange={(field, val) => setField(currentLot.code, field, val)}
            />
          ))}
        </main>
      </div>
    </div>
  );
}
